import React, { useState, useEffect } from 'react';

const CustomAlert = ({
  isOpen,
  onClose,
  title = "Alert",
  message,
  type = "info", // info, success, warning, error
  showCancel = false,
  onConfirm,
  confirmText = "OK",
  cancelText = "Cancel"
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    handleClose();
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          icon: '✓',
          iconColor: 'text-emerald-600',
          borderColor: 'border-emerald-100',
          bgColor: 'bg-emerald-50/70',
          buttonColor: 'bg-indigo-600 hover:bg-indigo-700',
          ringColor: 'focus:ring-indigo-500'
        };
      case 'warning':
        return {
          icon: '⚠',
          iconColor: 'text-amber-600',
          borderColor: 'border-amber-100',
          bgColor: 'bg-amber-50/70',
          buttonColor: 'bg-indigo-600 hover:bg-indigo-700',
          ringColor: 'focus:ring-indigo-500'
        };
      case 'error':
        return {
          icon: '✕',
          iconColor: 'text-rose-600',
          borderColor: 'border-rose-100',
          bgColor: 'bg-rose-50/70',
          buttonColor: 'bg-indigo-600 hover:bg-indigo-700',
          ringColor: 'focus:ring-indigo-500'
        };
      default:
        return {
          icon: 'ℹ',
          iconColor: 'text-[#0F2942]',
          borderColor: 'border-blue-100',
          bgColor: 'bg-blue-50/70',
          buttonColor: 'bg-[#0F2942] hover:bg-[#0B2033]',
          ringColor: 'focus:ring-[#0F2942]/50'
        };
    }
  };

  const styles = getTypeStyles();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center !mt-0 p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px] transition-opacity duration-300"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-md w-full transform transition-all duration-300 ${isVisible ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-2'
          }`}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b ${styles.borderColor} ${styles.bgColor} rounded-t-xl`}>
          <div className="flex items-center">
            <span className={`w-8 h-8 flex items-center justify-center rounded-full bg-white text-lg font-bold mr-3 ${styles.iconColor}`}>
              {styles.icon}
            </span>
            <h3 className="text-[18px] font-semibold text-slate-800 tracking-tight">
              {title}
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {typeof message === 'string' ? (
            <p className="text-[15px] leading-relaxed text-slate-600 whitespace-pre-line break-words first-letter:capitalize">
              {message}
            </p>
          ) : (
            <div className="text-[15px] leading-relaxed text-slate-600 break-words">
              {message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-[#f8fafc] rounded-b-2xl flex justify-end gap-3">
          {showCancel && (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-[14px] font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 transition-colors"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`min-w-[84px] px-5 py-2 text-[13px] font-semibold text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${styles.buttonColor} ${styles.ringColor}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomAlert;

